import React, { useState, useRef, useEffect } from "react";
import { Link2, ExternalLink, Mail, Loader2, MessageCircle, MessageSquare } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";

// Jun-2026 (User-Wunsch, 2. Runde) — Klick auf den QR-Code öffnet den Druck,
// kein separater Drucken/Kopieren-Button. Versand: WhatsApp & SMS (nur Link),
// E-Mail (QR + Link zusammen).
export default function PublicBookingLink() {
    const { user } = useAuth();
    const [mailBusy, setMailBusy] = useState(false);
    const [target, setTarget] = useState(null);
    const qrWrapRef = useRef(null);
    useEffect(() => {
        api.get("/workshop/booking-link").then((r) => setTarget(r.data)).catch(() => setTarget(null));
    }, []);
    if (!user?.id) return null;

    const url = `${window.location.origin}/werkstatt/${target?.dealer_id || user.id}/termin`;
    const dealerName = target?.label || user.company_name || user.name || "unsere Werkstatt";
    const shareText = `Buchen Sie Ihren Werkstatttermin bei ${dealerName} bequem online: ${url}`;

    const printQr = () => {
        const canvas = qrWrapRef.current?.querySelector("canvas");
        if (!canvas) return;
        const w = window.open("", "_blank", "noopener,width=480,height=640");
        if (!w) return;
        w.document.write(`<html><head><title>Werkstatt-Terminbuchung</title></head>
            <body style="font-family:Arial,sans-serif;text-align:center;padding:40px">
            <h2 style="margin-bottom:4px">Werkstatt-Termin online buchen</h2>
            <p style="color:#555;margin-top:0">${dealerName}</p>
            <img src="${canvas.toDataURL("image/png")}" style="width:260px;height:260px" alt="QR"/>
            <p style="font-size:12px;color:#555;word-break:break-all">${url}</p>
            </body></html>`);
        w.document.close();
        w.focus();
        setTimeout(() => { w.print(); }, 300);
    };

    const sendEmail = async () => {
        const to = window.prompt("E-Mail-Adresse des Kunden (QR-Code + Link werden zusammen gesendet):");
        if (!to) return;
        setMailBusy(true);
        try {
            await api.post("/workshop/booking-link/email", { to: to.trim(), origin: window.location.origin });
            toast.success(`Buchungslink + QR-Code an ${to.trim()} gesendet`);
        } catch (e) { toast.error(e?.response?.data?.detail || "E-Mail konnte nicht gesendet werden"); }
        setMailBusy(false);
    };

    const btn = "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm text-[11px] font-black uppercase tracking-wider transition-colors";
    return (
        <div className="bg-[#FFFBEB] border border-[#FFCC00] rounded-sm p-3" data-testid="public-booking-link">
            <div className="flex items-center gap-3 flex-wrap">
                <button type="button" onClick={printQr} ref={qrWrapRef}
                    className="bg-white p-1 rounded-sm border border-gray-200 shrink-0 hover:border-[#111827] hover:shadow transition-all cursor-pointer"
                    title="Klicken zum Ausdrucken — zum Aufkleben im Verkaufsraum"
                    data-testid="public-booking-qr-print">
                    <QRCodeCanvas value={url} size={56} level="M" includeMargin={false} data-testid="public-booking-qr" />
                </button>
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                        <Link2 className="w-3 h-3" /> Öffentlicher Buchungslink{target?.label ? ` — ${target.label}` : ""}
                    </div>
                    <a href={url} target="_blank" rel="noopener noreferrer"
                        className="text-sm font-mono-num text-[#111827] hover:underline break-all"
                        data-testid="public-booking-url">
                        {url}
                    </a>
                    <div className="text-[10px] text-gray-400 mt-0.5">QR-Code anklicken zum Ausdrucken.</div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <a href={url} target="_blank" rel="noopener noreferrer"
                        className={`${btn} bg-white border border-gray-300 text-[#111827] hover:bg-gray-50`}
                        data-testid="public-booking-open">
                        <ExternalLink className="w-3.5 h-3.5" /> Öffnen
                    </a>
                    <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer"
                        className={`${btn} bg-[#25D366] text-white hover:brightness-95`}
                        title="Buchungslink per WhatsApp senden (nur Link)"
                        data-testid="public-booking-whatsapp">
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                    <a href={`sms:?&body=${encodeURIComponent(shareText)}`}
                        className={`${btn} bg-white border border-amber-300 text-amber-800 hover:bg-amber-50`}
                        title="Buchungslink per SMS senden (nur Link)"
                        data-testid="public-booking-sms">
                        <MessageSquare className="w-3.5 h-3.5" /> SMS
                    </a>
                    <button type="button" onClick={sendEmail} disabled={mailBusy}
                        className={`${btn} bg-[#111827] text-[#FFCC00] hover:bg-black disabled:opacity-50`}
                        title="QR-Code und Link zusammen per E-Mail an den Kunden senden"
                        data-testid="public-booking-email">
                        {mailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} E-Mail
                    </button>
                </div>
            </div>
        </div>
    );
}
